<script lang='ts'>
import { reactive, defineComponent, ref, watch } from '@vue/composition-api';
import { required } from '@/_core/utils/forms/validators';
import { useForm, useAsyncValidation } from '@/_core/utils/forms';
import { queryExecutor, commandDispatcher } from '@/_core/cqrs';
import { PasswordPolicyQuery, PasswordPolicyQueryResult } from '@/api/account/queries';
import { ChangePassword } from '@/api/account/commands';
import { snackbar } from '@/_core/app-components/use';

const ChangePasswordForm = defineComponent({
    name: 'ChangePasswordForm',

    setup(props, ctx) {
        const state = reactive({
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
            passwordPolicyResult: { IsValid: true } as PasswordPolicyQueryResult,
        });

        const formElement = ref('');
        const form = useForm(formElement, { required });
        const checkNewPassword = async () => {
            if (state.confirmNewPassword.length > 0 && state.confirmNewPassword != state.newPassword) {
                return 'Passwords do not match!';
            }
            return;
        };
        const confirmNewPasswordErrors = useAsyncValidation(() => [state.confirmNewPassword, state.newPassword], checkNewPassword);
        const currentPasswordErrors = ref([]);
        //Azért mert ha rosszul írja be akkor mentéskor kap errort, de a form így invalid lesz, és nem lehet megnyomni a save-et többet...
        watch(
            () => state.currentPassword,
            () => {
                currentPasswordErrors.value = [];
            },
            { immediate: true }
        );

        const save = async () => {
            const valRes = await queryExecutor.executeQuery(new PasswordPolicyQuery(state.newPassword));
            if (valRes.IsValid == false) {
                state.passwordPolicyResult = valRes;
                return false;
            }

            const res = await commandDispatcher.dispatchCommand(new ChangePassword(state.newPassword, state.currentPassword));
            if (res.IsSuccess) {
                snackbar.showSuccess('Your password successfully changed!');
                close();
                return true;
            } else {
                if (res.IsCurrentPasswordInvalid) {
                    currentPasswordErrors.value = [res.Message];
                }
                snackbar.showError(res.Message);
                return false;
            }
        };

        const onSave = async () => {
            await form.saveForm(save);
        };

        const close = () => {
            ctx.emit('close');
        };

        return {
            state,
            form,
            formElement,
            confirmNewPasswordErrors,
            currentPasswordErrors,
            close,
            onSave,
        };
    },
});

export default ChangePasswordForm;
</script>

<template>
  <v-card flat class="pt-2">
    <v-progress-linear
      :active="form.isSaving"
      indeterminate        
      color="primary lighten-2"
      class="mt-1"
    />

    <v-card-text>
      <v-alert
        v-if="!state.passwordPolicyResult.IsValid"
        type="warning"
        text
        prominent
      >
        Your password does not satisfy the current password policy requirements.
        <ul>
          <li v-if="state.passwordPolicyResult.IsMinCharCountInvalid">
            {{ state.passwordPolicyResult.MinCharCount }} characters
          </li>
          
          <li v-if="state.passwordPolicyResult.IsMinUpperCharCountInvalid">
            {{ state.passwordPolicyResult.MinUpperCharCount }} uppercase letters
          </li>
                  
          <li v-if="state.passwordPolicyResult.IsMinLowerCharCountInvalid">
            {{ state.passwordPolicyResult.MinLowerCharCount }} lowercase letters
          </li>
         
          <li v-if="state.passwordPolicyResult.IsMinNumberCountInvalid">
            {{ state.passwordPolicyResult.MinNumberCount }} digits
          </li>

          <li v-if="state.passwordPolicyResult.IsMinSpecialCharsCountInvalid">
            {{ state.passwordPolicyResult.MinSpecialCharsCount }} special characters
          </li>

          <li v-if="state.passwordPolicyResult.DifferentPasswordRequired">
            must be different than a previous {{ state.passwordPolicyResult.DifferentPasswordCount }}
          </li>
        </ul>
      </v-alert>
     
      <v-form
        ref="formElement"
        v-model="form.valid"
        :lazy-validation="true"
        @submit.prevent="onSave"
        @keydown.native.enter.prevent="onSave"
        @keydown.native.esc.stop
      >
        <AppPasswordField 
          v-model="state.currentPassword"
          :rules="[form.rules.required()]"
          :error-messages="currentPasswordErrors"
          :disabled="form.isSaving"
          label="Current password"      
        />
       
        <AppPasswordField 
          v-model="state.newPassword"
          :rules="[form.rules.required()]"
          :disabled="form.isSaving"
          :error-messages="confirmNewPasswordErrors"
          class="mt-3"
          label="New password"
        />       

        <AppPasswordField 
          v-model="state.confirmNewPassword"
          :rules="[form.rules.required()]"
          :error-messages="confirmNewPasswordErrors"
          :disabled="form.isSaving"
          class="mt-3"
          label="Confirm new password"
        />
      </v-form>
    </v-card-text>

    <v-card-actions class="pt-0">
      <v-btn
        color="primary"
        depressed
        :loading="form.isSaving"
        :disabled="!form.valid"
        @click="onSave"
      >
        Save
      </v-btn>
      <v-btn
        depressed 
        color="secondaryButton"
        class="primary--text"
        @click="close"
      >
        Cancel
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style lang='sass' scoped></style>